/**
 * Currency formatting core — the SINGLE place in the app that builds a
 * currency `Intl.NumberFormat`. Every money string flows through here.
 *
 * Backend returns monetary values as D6 strings (1 USDC = 1_000_000). These
 * utilities convert D6 strings (or display-unit numbers) to localised display
 * strings, applying the USDC→local exchange rate exactly once and formatting
 * with the active locale (full CLDR — grouping, decimal mark and symbol
 * placement follow the locale; the currency is the user's choice).
 *
 * Consolidated from the former `lib/format/format-d6.ts` and the price helpers
 * in `lib/format/format-number.ts`. Do not add a second currency
 * `Intl.NumberFormat` anywhere — extend this module (enforced by FQ-091/160).
 */

import { getCurrencyByCode } from '@/config/currencies';
import { D6_SCALE as D6_SCALE_NUMBER, D9_SCALE as D9_SCALE_NUMBER } from '@/config/solana';
export type FormatMode =
  | 'full'
  | 'compact'
  /** Like `compact` with at most one fraction digit (`$18.1K` vs `$18K`) — narrow KPI cells. */
  | 'compactTight'
  /** Compact with a fixed two fraction digits (`$130.20K`) — live-ticking money. */
  | 'compactDecimal'
  /** Compact with NO fraction digits (`$10K`, `$673`) — glanceable stat chips. */
  | 'compactWhole'
  | 'short'
  | 'clean'
  /** Full notation, no fraction digits (`$1,929`). */
  | 'whole'
  /** Drop cents at/above $100, keep them below, compact past the threshold. */
  | 'adaptive'
  | 'price'
  /** Like `price` but with an EXACT (non-trimmed) fraction-digit count — the
   *  chart y-axis uses it so every tick shares the same decimal precision. */
  | 'priceFixed';

export interface FormatOptions {
  currency?: string;
  rate?: number;
  /** Active next-intl locale — drives grouping, decimal mark, symbol placement. */
  locale?: string;
  mode?: FormatMode;
}

type InternalFormatMode = FormatMode | 'marketCapAxis';

const ZERO_DECIMAL_COMPACT_THRESHOLD = 10_000;
const TWO_DECIMAL_COMPACT_THRESHOLD = 100_000;
const ADAPTIVE_WHOLE_THRESHOLD = 100;
/** Locale used when a caller does not supply one (SSR / non-hook paths). */
const DEFAULT_LOCALE = 'en-US';
const D6_SCALE: bigint = BigInt(D6_SCALE_NUMBER);
const D9_SCALE: bigint = BigInt(D9_SCALE_NUMBER);
const COMPACT_SUFFIX_THRESHOLD = 1_000;

const formatterCache: Map<string, Intl.NumberFormat> = new Map();

function getOrCreateFormatter(
  currency: string,
  locale: string,
  mode: InternalFormatMode,
  fractionDigits?: number,
): Intl.NumberFormat {
  const key = `${locale}-${currency}-${mode}${fractionDigits !== undefined ? `-${String(fractionDigits)}` : ''}`;
  const cached = formatterCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const currencyInfo = getCurrencyByCode(currency);
  const decimals = currencyInfo?.decimals ?? 2;

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
  };

  if (mode === 'compact' || mode === 'short') {
    options.notation = 'compact';
    options.compactDisplay = 'short';
  } else if (mode === 'compactTight') {
    options.notation = 'compact';
    options.compactDisplay = 'short';
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 1;
  } else if (mode === 'compactDecimal') {
    options.notation = 'compact';
    options.compactDisplay = 'short';
    options.minimumFractionDigits = 2;
    options.maximumFractionDigits = 2;
  } else if (mode === 'compactWhole') {
    options.notation = 'compact';
    options.compactDisplay = 'short';
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
  } else if (mode === 'marketCapAxis' && fractionDigits !== undefined) {
    options.notation = 'compact';
    options.compactDisplay = 'short';
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = fractionDigits;
  } else if (mode === 'clean') {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = decimals;
  } else if (mode === 'whole') {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
  } else if (mode === 'price' && fractionDigits !== undefined) {
    options.minimumFractionDigits = Math.min(decimals, fractionDigits);
    options.maximumFractionDigits = fractionDigits;
  } else if (mode === 'priceFixed' && fractionDigits !== undefined) {
    // Fixed precision — min === max so trailing zeros are kept ($0.0500, not
    // $0.05), which is what keeps a y-axis column's decimals uniform.
    options.minimumFractionDigits = fractionDigits;
    options.maximumFractionDigits = fractionDigits;
  } else {
    options.minimumFractionDigits = decimals;
    options.maximumFractionDigits = decimals;
  }

  const formatter = new Intl.NumberFormat(locale, options);
  formatterCache.set(key, formatter);
  return formatter;
}

/** Fraction digits for share-price-style values (sub-cent to ~$1). */
function priceFractionDigits(absValue: number): number {
  if (absValue === 0) return 4;
  if (absValue >= 1) return 2;
  if (absValue >= 0.01) return 4;
  return 6;
}

function shouldUseCompact(absValue: number, currency: string): boolean {
  const decimals = getCurrencyByCode(currency)?.decimals ?? 2;
  const threshold = decimals === 0 ? ZERO_DECIMAL_COMPACT_THRESHOLD : TWO_DECIMAL_COMPACT_THRESHOLD;
  return absValue >= threshold;
}

function resolveFormatter(
  numeric: number,
  currency: string,
  locale: string,
  requestedMode: FormatMode | undefined,
): Intl.NumberFormat {
  if (requestedMode === 'price') {
    return getOrCreateFormatter(currency, locale, 'price', priceFractionDigits(Math.abs(numeric)));
  }
  if (requestedMode === 'adaptive') {
    const abs = Math.abs(numeric);
    if (shouldUseCompact(abs, currency)) return getOrCreateFormatter(currency, locale, 'compact');
    if (abs >= ADAPTIVE_WHOLE_THRESHOLD) return getOrCreateFormatter(currency, locale, 'whole');
    return getOrCreateFormatter(currency, locale, 'full');
  }
  if (requestedMode !== undefined) {
    return getOrCreateFormatter(currency, locale, requestedMode);
  }
  if (shouldUseCompact(Math.abs(numeric), currency)) {
    return getOrCreateFormatter(currency, locale, 'compact');
  }
  return getOrCreateFormatter(currency, locale, 'full');
}

function applyRate(numeric: number, opts: FormatOptions | undefined): number {
  if (opts?.rate !== undefined && opts.rate > 0) {
    return numeric * opts.rate;
  }
  return numeric;
}

function currencyOf(opts: FormatOptions | undefined): string {
  return opts?.currency ?? 'USD';
}

function localeOf(opts: FormatOptions | undefined): string {
  return opts?.locale ?? DEFAULT_LOCALE;
}

function parseD6(value: string): bigint | null {
  if (!/^(?:0|-?[1-9]\d*)$/.test(value)) return null;
  return BigInt(value);
}

interface DecimalRatio {
  numerator: bigint;
  denominator: bigint;
}

interface DecimalAmount {
  numerator: bigint;
  denominator: bigint;
}

function decimalRatio(value: number): DecimalRatio | null {
  const match = value.toString().match(/^(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i);
  if (match === null || match[1] === undefined) return null;
  const fraction = match[2] ?? '';
  const exponent = Number(match[3] ?? '0');
  if (!Number.isSafeInteger(exponent)) return null;
  const digits = BigInt(`${match[1]}${fraction}`);
  const decimalPlaces = fraction.length - exponent;
  return decimalPlaces >= 0
    ? { numerator: digits, denominator: BigInt(10) ** BigInt(decimalPlaces) }
    : {
        numerator: digits * BigInt(10) ** BigInt(-decimalPlaces),
        denominator: BigInt(1),
      };
}

function localFixedPointAmount(
  value: bigint,
  scale: bigint,
  opts: FormatOptions | undefined,
): DecimalAmount {
  const rate = opts?.rate;
  if (rate === undefined || !Number.isFinite(rate) || rate <= 0 || rate === 1) {
    return { numerator: value, denominator: scale };
  }
  const ratio = decimalRatio(rate);
  if (ratio === null) return { numerator: value, denominator: scale };
  return {
    numerator: value * ratio.numerator,
    denominator: scale * ratio.denominator,
  };
}

function absAmount(amount: DecimalAmount): DecimalAmount {
  return {
    numerator: amount.numerator < BigInt(0) ? -amount.numerator : amount.numerator,
    denominator: amount.denominator,
  };
}

function amountAtLeast(amount: DecimalAmount, threshold: number): boolean {
  return absAmount(amount).numerator >= BigInt(threshold) * amount.denominator;
}

function shouldUseCompactAmount(amount: DecimalAmount, currency: string): boolean {
  const decimals = getCurrencyByCode(currency)?.decimals ?? 2;
  const threshold = decimals === 0 ? ZERO_DECIMAL_COMPACT_THRESHOLD : TWO_DECIMAL_COMPACT_THRESHOLD;
  return amountAtLeast(amount, threshold);
}

function priceFractionDigitsAmount(amount: DecimalAmount): number {
  const magnitude = absAmount(amount).numerator;
  if (magnitude === BigInt(0)) return 4;
  if (magnitude >= amount.denominator) return 2;
  if (magnitude * BigInt(100) >= amount.denominator) return 4;
  return 6;
}

function formatLocalAmount(amount: DecimalAmount, opts: FormatOptions | undefined): string {
  const currency = currencyOf(opts);
  const locale = localeOf(opts);
  const requestedMode = opts?.mode;

  if (
    requestedMode === 'compact' ||
    requestedMode === 'compactTight' ||
    requestedMode === 'compactDecimal' ||
    requestedMode === 'compactWhole' ||
    requestedMode === 'short'
  ) {
    return formatCompactAmount(amount, currency, locale, requestedMode);
  }
  if (requestedMode === 'adaptive') {
    if (shouldUseCompactAmount(amount, currency)) {
      return formatCompactAmount(amount, currency, locale, 'compact');
    }
    if (amountAtLeast(amount, ADAPTIVE_WHOLE_THRESHOLD)) {
      return formatFixedAmount(amount, currency, locale, 0, 0);
    }
  } else if (requestedMode === undefined && shouldUseCompactAmount(amount, currency)) {
    return formatCompactAmount(amount, currency, locale, 'compact');
  }

  const currencyDecimals = getCurrencyByCode(currency)?.decimals ?? 2;
  if (requestedMode === 'whole') return formatFixedAmount(amount, currency, locale, 0, 0);
  if (requestedMode === 'clean') {
    return formatFixedAmount(amount, currency, locale, 0, currencyDecimals);
  }
  if (requestedMode === 'price') {
    const digits = priceFractionDigitsAmount(amount);
    return formatFixedAmount(amount, currency, locale, Math.min(currencyDecimals, digits), digits);
  }
  return formatFixedAmount(amount, currency, locale, currencyDecimals, currencyDecimals);
}

function formatCompactAmount(
  amount: DecimalAmount,
  currency: string,
  locale: string,
  mode: 'compact' | 'compactTight' | 'compactDecimal' | 'compactWhole' | 'short',
): string {
  if (!amountAtLeast(amount, COMPACT_SUFFIX_THRESHOLD)) {
    const currencyDecimals = getCurrencyByCode(currency)?.decimals ?? 2;
    if (mode === 'compactTight') return formatFixedAmount(amount, currency, locale, 0, 1);
    if (mode === 'compactDecimal') return formatFixedAmount(amount, currency, locale, 2, 2);
    if (mode === 'compactWhole') return formatFixedAmount(amount, currency, locale, 0, 0);
    return formatFixedAmount(amount, currency, locale, currencyDecimals, currencyDecimals);
  }
  return getOrCreateFormatter(currency, locale, mode).format(decimalAmountString(amount));
}

function decimalAmountString(amount: DecimalAmount): Intl.StringNumericLiteral {
  const magnitude = absAmount(amount).numerator;
  const whole = magnitude / amount.denominator;
  const decimalPlaces = powerOfTenDecimalPlaces(amount.denominator);
  const fraction = (magnitude % amount.denominator)
    .toString()
    .padStart(decimalPlaces, '0')
    .replace(/0+$/, '');
  const sign = amount.numerator < BigInt(0) ? '-' : '';
  const decimal =
    fraction.length === 0 ? `${sign}${whole.toString()}` : `${sign}${whole.toString()}.${fraction}`;
  if (!isIntlNumericLiteral(decimal)) throw new Error('invalid canonical currency decimal');
  return decimal;
}

function powerOfTenDecimalPlaces(value: bigint): number {
  const decimal = value.toString();
  if (!/^10*$/.test(decimal)) throw new Error('currency amount denominator must be a power of ten');
  return decimal.length - 1;
}

function isIntlNumericLiteral(value: string): value is Intl.StringNumericLiteral {
  return /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value);
}

function formatFixedAmount(
  amount: DecimalAmount,
  currency: string,
  locale: string,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
): string {
  const fractionScale = BigInt(10) ** BigInt(maximumFractionDigits);
  const magnitude = absAmount(amount).numerator;
  const scaled = divideRoundHalfUp(magnitude * fractionScale, amount.denominator);
  const whole = maximumFractionDigits === 0 ? scaled : scaled / fractionScale;
  const rawFraction =
    maximumFractionDigits === 0
      ? ''
      : (scaled % fractionScale).toString().padStart(maximumFractionDigits, '0');
  const fraction = rawFraction.replace(
    new RegExp(`0{0,${String(maximumFractionDigits - minimumFractionDigits)}}$`),
    '',
  );
  const formatter = getOrCreateFormatter(currency, locale, 'priceFixed', fraction.length);
  const signedWhole: bigint | number =
    amount.numerator < BigInt(0) ? (whole === BigInt(0) ? -0 : -whole) : whole;
  return formatter
    .formatToParts(signedWhole)
    .map((part) => (part.type === 'fraction' ? fraction : part.value))
    .join('');
}

function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  return remainder * BigInt(2) >= denominator ? quotient + BigInt(1) : quotient;
}

/**
 * Convert a D6 USDC string to a display string.
 * @example formatD6("1500000") → "$1.50"
 */
export function formatD6(value: string, opts?: FormatOptions): string {
  const parsed = parseD6(value) ?? BigInt(0);
  return formatLocalAmount(localFixedPointAmount(parsed, D6_SCALE, opts), opts);
}

/** Format an exact D9 outcome/share price without a JavaScript-number round trip. */
export function formatD9(value: string, opts?: FormatOptions): string {
  const parsed = parseD6(value) ?? BigInt(0);
  const priceOptions: FormatOptions = { ...opts, mode: opts?.mode ?? 'price' };
  return formatLocalAmount(localFixedPointAmount(parsed, D9_SCALE, priceOptions), priceOptions);
}

/** Convert a D6 USDC string to a signed display string (positive gets `+`). */
export function formatD6Signed(value: string, opts?: FormatOptions): string {
  const parsed = parseD6(value) ?? BigInt(0);
  const amount = localFixedPointAmount(parsed, D6_SCALE, opts);
  const formatted = formatLocalAmount(absAmount(amount), opts);
  if (amount.numerator > BigInt(0)) return `+${formatted}`;
  if (amount.numerator < BigInt(0)) return `-${formatted}`;
  return formatted;
}

/**
 * Format a numeric balance (already in display units, not D6) to a currency string.
 * @example formatBalance(150.50, { currency: 'EUR', rate: 0.92 }) → "€138.46"
 */
export function formatBalance(value: number, opts?: FormatOptions): string {
  const numeric = applyRate(value, opts);
  return resolveFormatter(numeric, currencyOf(opts), localeOf(opts), opts?.mode).format(numeric);
}

/**
 * Format a Market Cap y-axis tick using the renderer's actual numeric step.
 * Compact precision follows that step in the tick's displayed unit, while a
 * zero minimum trims redundant decimals from whole ticks (`$1K`, not `$1.00K`).
 */
export function formatMarketCapAxis(value: number, tickStep: number, opts?: FormatOptions): string {
  const numeric = applyRate(value, opts);
  const numericStep = Math.abs(applyRate(tickStep, opts));
  const currency = currencyOf(opts);
  const locale = localeOf(opts);
  const unitDivisor = compactUnitDivisor(numeric, currency, locale);
  const fractionDigits = fractionDigitsForStep(numericStep / unitDivisor);
  return getOrCreateFormatter(currency, locale, 'marketCapAxis', fractionDigits).format(numeric);
}

function compactUnitDivisor(value: number, currency: string, locale: string): number {
  const magnitude = Math.abs(value);
  if (!Number.isFinite(magnitude) || magnitude < COMPACT_SUFFIX_THRESHOLD) return 1;

  const parts = getOrCreateFormatter(currency, locale, 'marketCapAxis', 8).formatToParts(magnitude);
  if (!parts.some((part) => part.type === 'compact')) return 1;

  const digitMap = localizedDigitMap(locale);
  const integer = parts
    .filter((part) => part.type === 'integer')
    .map((part) => normalizeDigits(part.value, digitMap))
    .join('');
  const fraction = parts
    .filter((part) => part.type === 'fraction')
    .map((part) => normalizeDigits(part.value, digitMap))
    .join('');
  const mantissa = Number(fraction.length === 0 ? integer : `${integer}.${fraction}`);
  return Number.isFinite(mantissa) && mantissa > 0 ? magnitude / mantissa : 1;
}

const localizedDigitsCache: Map<string, ReadonlyMap<string, string>> = new Map();

function localizedDigitMap(locale: string): ReadonlyMap<string, string> {
  const cached = localizedDigitsCache.get(locale);
  if (cached !== undefined) return cached;

  const formatter = new Intl.NumberFormat(locale, { useGrouping: false });
  const digits = new Map<string, string>();
  for (let digit = 0; digit <= 9; digit++) {
    digits.set(formatter.format(digit), String(digit));
  }
  localizedDigitsCache.set(locale, digits);
  return digits;
}

function normalizeDigits(value: string, digitMap: ReadonlyMap<string, string>): string {
  return Array.from(value, (digit) => digitMap.get(digit) ?? digit).join('');
}

function fractionDigitsForStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  if (step >= 1) return 0;

  const leadingFractionDigits = Math.max(0, Math.ceil(-Math.log10(step)));
  const scaled = step * 10 ** leadingFractionDigits;
  const isSingleSignificantDigit =
    Number.isFinite(scaled) && Math.abs(scaled - Math.round(scaled)) <= 1e-9;
  return Math.min(8, leadingFractionDigits + (isSingleSignificantDigit ? 0 : 1));
}

/**
 * Format a share/outcome price (a display-unit dollar value, e.g. $0.42).
 * Applies the rate, picks adaptive sub-cent precision, and localises symbol +
 * grouping. Replaces the former `formatPrice` / `formatChartPrice`, which
 * hard-coded `$` and skipped the exchange rate.
 */
export function formatPrice(value: number, opts?: FormatOptions): string {
  const numeric = applyRate(value, opts);
  if (Math.abs(numeric) < 1e-10) {
    return getOrCreateFormatter(currencyOf(opts), localeOf(opts), 'price', 2).format(0);
  }
  return getOrCreateFormatter(
    currencyOf(opts),
    localeOf(opts),
    'price',
    priceFractionDigits(Math.abs(numeric)),
  ).format(numeric);
}

/**
 * Format a price with an EXACT fraction-digit count (no trailing-zero trimming).
 * Used by the chart y-axis so every tick label shares the same decimal
 * precision (`$0.0500`, `$0.0530`, …) regardless of the individual value.
 * `fractionDigits` is clamped to a sane [0, 8] range. Rate + locale + currency
 * are applied exactly like {@link formatPrice}.
 */
export function formatPriceFixed(
  value: number,
  fractionDigits: number,
  opts?: FormatOptions,
): string {
  const numeric = applyRate(value, opts);
  const digits = Math.min(8, Math.max(0, Math.round(fractionDigits)));
  return getOrCreateFormatter(currencyOf(opts), localeOf(opts), 'priceFixed', digits).format(
    numeric,
  );
}

// ── Quick-amount label formatting ────────────────────────────────────────────
// Formats an ALREADY-LOCAL value (no exchange rate applied) for quick-amount
// button labels. `minimumFractionDigits: 0` so round amounts display cleanly
// ($5, not $5.00).

const quickCompactCache: Map<string, Intl.NumberFormat> = new Map();
const quickFullCache: Map<string, Intl.NumberFormat> = new Map();

function getQuickCompactFormatter(locale: string, currencyCode: string): Intl.NumberFormat {
  const key = `${locale}-${currencyCode}`;
  const cached = quickCompactCache.get(key);
  if (cached !== undefined) return cached;
  const fmt = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    notation: 'compact',
    compactDisplay: 'short',
  });
  quickCompactCache.set(key, fmt);
  return fmt;
}

function getQuickFullFormatter(
  locale: string,
  currencyCode: string,
  decimals: number,
): Intl.NumberFormat {
  const key = `${locale}-${currencyCode}-${String(decimals)}`;
  const cached = quickFullCache.get(key);
  if (cached !== undefined) return cached;
  const fmt = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  quickFullCache.set(key, fmt);
  return fmt;
}

/**
 * Format a quick-amount button label. Value is already in local currency
 * (pre-scaled). Small amounts use full formatting ($5, ¥500); large amounts
 * use compact notation ($1.5K) so buttons stay readable.
 */
export function formatQuickAmount(
  value: number,
  currencyCode: string,
  decimals: number,
  locale: string = DEFAULT_LOCALE,
): string {
  const compactThreshold = decimals === 0 ? 10_000 : 100_000;
  if (value >= compactThreshold) {
    return getQuickCompactFormatter(locale, currencyCode).format(value);
  }
  return getQuickFullFormatter(locale, currencyCode, decimals).format(value);
}

/** Currency symbol placement for amount input chrome, without converting monetary values. */
export function currencyParts(locale: string, currency: string): Intl.NumberFormatPart[] {
  return getOrCreateFormatter(currency, locale, 'full').formatToParts(0);
}
